import { del, get, set } from 'idb-keyval';
import { useCallback, useEffect, useState } from 'react';

// Temporary key value storage using IndexedDB to remember state even after page refresh
const useTemporaryKeyValueStorage = <T>(
	key: string,
	defaultValue: T,
	cacheTimeInSeconds: number = 60 * 60 * 1000
) => {
	const [value, setInternalValue] = useState(defaultValue);

	useEffect(() => {
		get(`temp-${key}`).then((value) => {
			// If there is no value, return
			if (!value) return;
			// If there is a value and it's less than 1 hour old, set the value
			if (
				new Date().getTime() -
					new Date(JSON.parse(value).updatedAt).getTime() <
				cacheTimeInSeconds
			) {
				setInternalValue(JSON.parse(value).value);
			} else {
				// If the value is older than 1 hour, delete it
				del(`temp-${key}`);
			}
		});
	}, [cacheTimeInSeconds, key]);

	const setValue = useCallback(
		(value: T) => {
			set(
				`temp-${key}`,
				JSON.stringify({
					value,
					updatedAt: new Date().toISOString(),
				})
			);
			setInternalValue(value);
		},
		[key]
	);

	const deleteValue = useCallback(() => {
		del(`temp-${key}`);
		setInternalValue(defaultValue);
	}, [key, defaultValue]);

	return {
		value,
		setValue,
		deleteValue,
	};
};

export default useTemporaryKeyValueStorage;
